import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BarChart2, MessageSquare, Expand } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const courseActivityConfig = {
  active: {
    label: 'Active Conversations',
    color: 'hsl(var(--chart-3))',
    icon: MessageSquare,
  },
  total: {
    label: 'Total Conversations',
    color: 'hsl(var(--chart-5))',
    icon: BarChart2,
  },
} satisfies ChartConfig

type CourseActivityChartProps = {
  courseChartData: { name: string; active: number; total: number; users: number }[]
  loading: boolean
}

const CourseActivityChart = ({ courseChartData, loading }: CourseActivityChartProps) => {
  return (
    <Dialog>
      <Card className="w-full min-w-0">
        <CardHeader className="flex flex-col items-start justify-between space-y-0 pb-2">
          <div className="flex items-center justify-between w-full">
            <CardTitle>Course Activity Distribution</CardTitle>
            <DialogTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 data-[state=open]:bg-muted" aria-label="Open">
                <Expand className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </div>
          <CardDescription>Active (last 30 days) vs Total Conversations per Course</CardDescription>
        </CardHeader>
        <CardContent className="min-w-0">
          {loading ? (
            <Skeleton className="h-[18.75rem] w-full" />
          ) : (
            <ChartContainer config={courseActivityConfig} className="h-[18.75rem] w-full min-w-0 overflow-hidden">
              <BarChart accessibilityLayer data={courseChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => value.slice(0, 3)}
                />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar dataKey="active" fill="var(--color-active)" radius={4} />
                <Bar dataKey="total" fill="var(--color-total)" radius={4} />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>
      <DialogContent className="sm:max-w-[90%] max-h-[80vh] overflow-auto">
        <DialogHeader>
          <CardHeader className="p-0">
            <DialogTitle asChild>
              <CardTitle>Course Activity Distribution</CardTitle>
            </DialogTitle>
            <DialogDescription asChild>
              <CardDescription>Active (last 30 days) vs Total Conversations per Course</CardDescription>
            </DialogDescription>
          </CardHeader>
        </DialogHeader>
        {loading ? (
          <Skeleton className="h-[37.5rem] w-full" />
        ) : (
          <ChartContainer config={courseActivityConfig} className="h-[37.5rem] w-full">
            <BarChart data={courseChartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => value.slice(0, 3)}
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar dataKey="active" fill="var(--color-active)" radius={4} />
              <Bar dataKey="total" fill="var(--color-total)" radius={4} />
            </BarChart>
          </ChartContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CourseActivityChart
